import { ProductPageTemplate } from "@/components/site/ProductPageTemplate";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    metaTitle: "Trailer Suspension Parts in Elkhart, IN - Springs & Hangers",
    metaDescription: "Trailer suspension parts in Elkhart, IN: leaf springs, hangers, equalizers, shackles, and hardware. Double-eye and slipper systems, genuine Dexter, in stock.",
    heroEyebrow: "Springs · Hangers · Equalizers",
    heroHeading: "What keeps the load level - and the ride honest.",
    heroLede: "Trailer suspension is the most overlooked wear point. We stock the springs, hangers, equalizers, and hardware that fail first, plus the conversion kits to upgrade slipper to double-eye when it's time.",
    intro: "When a trailer pulls heavy on one side, sags at the rear, or starts hammering pothole hits, the suspension is almost always the answer. We carry full Dexter suspension components and complete tandem-axle kits - and we'll talk through whether to repair in place or upgrade the system.",
    features: [
      "Double-eye leaf springs - 4, 5, 6, 7-leaf",
      "Slipper-style leaf springs",
      "Equalizers - standard and heavy-duty",
      "Hangers - front, rear, center",
      "Shackles, bolts, bushings, and hardware kits",
      "Wet bolt kits for grease-able pivot points",
      "Heavy-duty equalizer upgrade kits",
      "Complete tandem-axle suspension rebuild kits"
    ],
    capacityTable: [
      { label: "Spring capacities", value: "1,750 – 8,000 lb each" },
      { label: "Systems supported", value: "Double-eye · Slipper" },
      { label: "Axle counts", value: "Single · Tandem · Triple" },
      { label: "Brand", value: "Dexter (OEM) + hardware" }
    ],
    variants: [
      { name: "Double-eye system", desc: "The premium configuration - both ends of each spring pivot on bolts. Smoother ride, easier service." },
      { name: "Slipper system", desc: "Common on lighter and budget trailers. Rear of spring slides in a hanger. We carry and convert both." },
      { name: "Heavy-duty equalizer", desc: "Reduces tandem rocking and protects springs on rough roads. A common upgrade for boat and dump trailers." },
      { name: "Wet-bolt kit", desc: "Grease-able pivot bolts and bushings - extends suspension life dramatically. Recommended on every rebuild." }
    ],
    related: [
      { title: "Hubs, Drums & Brakes", desc: "The other half of the wheel-end." },
      { title: "Trailer Repair", desc: "We install what we sell." },
      { title: "Jacks & Couplers", desc: "Tongue components." }
    ]
  },
  es: {
    metaTitle: "Partes de suspensión para remolques en Elkhart, IN - Ballestas y soportes",
    metaDescription: "Partes de suspensión para remolques en Elkhart, IN: ballestas, soportes, ecualizadores, grilletes y tornillería. Sistemas de ojo doble y tipo slipper, Dexter original, en existencia.",
    heroEyebrow: "Ballestas · Soportes · Ecualizadores",
    heroHeading: "Lo que mantiene la carga nivelada y el viaje seguro.",
    heroLede: "La suspensión del remolque es el punto de desgaste más ignorado. Tenemos en existencia las ballestas, soportes, ecualizadores y tornillería que fallan primero, además de kits de conversión para actualizar de tipo slipper a ojo doble cuando sea el momento.",
    intro: "Cuando un remolque tira pesado hacia un lado, se hunde en la parte trasera o comienza a golpear fuerte en los baches, la suspensión casi siempre es la respuesta. Contamos con componentes de suspensión Dexter completos y kits completos para ejes tándem, y le asesoraremos sobre si reparar en el sitio o actualizar el sistema.",
    features: [
      "Ballestas de ojo doble - 4, 5, 6, 7 hojas",
      "Ballestas tipo slipper",
      "Ecualizadores - estándar y de servicio pesado",
      "Soportes - delantero, trasero, central",
      "Grilletes, pernos, bujes y kits de tornillería",
      "Kits de pernos húmedos para puntos de pivote engrasables",
      "Kits de actualización de ecualizador de servicio pesado",
      "Kits completos de reconstrucción de suspensión para ejes tándem"
    ],
    capacityTable: [
      { label: "Capacidades de ballestas", value: "1,750 – 8,000 lb cada una" },
      { label: "Sistemas soportados", value: "Ojo doble · Slipper" },
      { label: "Cantidad de ejes", value: "Sencillo · Tándem · Triple" },
      { label: "Marca", value: "Dexter (OEM) + tornillería" }
    ],
    variants: [
      { name: "Sistema de ojo doble", desc: "La configuración premium - ambos extremos de cada ballesta pivotan sobre pernos. Viaje más suave, servicio más fácil." },
      { name: "Sistema Slipper", desc: "Común en remolques más ligeros y económicos. La parte trasera de la ballesta se desliza en un soporte. Vendemos y convertimos ambos." },
      { name: "Ecualizador de servicio pesado", desc: "Reduce el balanceo en tándem y protege las ballestas en caminos difíciles. Una actualización común para remolques de botes y de volteo." },
      { name: "Kit de perno húmedo", desc: "Pernos de pivote engrasables y bujes - extiende drásticamente la vida útil de la suspensión. Recomendado en cada reconstrucción." }
    ],
    related: [
      { title: "Mazas, tambores y frenos", desc: "La otra mitad del conjunto de la rueda." },
      { title: "Reparación de remolques", desc: "Instalamos lo que vendemos." },
      { title: "Gatos y acopladores", desc: "Componentes del enganche." }
    ]
  }
};

export default function Suspension() {
  const t = useCopy(COPY);
  
  return (
    <ProductPageTemplate
      slug="suspension-parts"
      title="Suspension Parts"
      metaTitle={t.metaTitle}
      metaDescription={t.metaDescription}
      hero={{
        eyebrow: t.heroEyebrow,
        heading: t.heroHeading,
        lede: t.heroLede,
      }}
      intro={t.intro}
      features={t.features}
      capacityTable={t.capacityTable}
      variants={t.variants}
      related={[
        { title: t.related[0].title, path: "/products/hubs/", desc: t.related[0].desc },
        { title: t.related[1].title, path: "/services/axle-repair/", desc: t.related[1].desc },
        { title: t.related[2].title, path: "/products/jacks-couplers/", desc: t.related[2].desc },
      ]}
    />
  );
}
